import {FormEvent, useEffect, useMemo, useState} from "react";
import {Alert, Badge, Button, Card, Form, InputGroup, Table} from "react-bootstrap";
import {NewsApi} from "../../api/NewsApi";
import {ApiError} from "../../api/http";
import {ProductsApi} from "../../api/ProductsApi";
import {NewsAudience, NewsCampaign, NewsSubscriberView, ProductViewModel} from "../../api/models";
import {ConfirmModal} from "../../components/ConfirmModal";
import {ErrorAlert, fieldError} from "../../components/ErrorAlert";
import {Field, FieldControl} from "../../components/Field";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {RichText} from "../../components/RichText";
import {RichTextEditor} from "../../components/RichTextEditor";
import {SubmitButton} from "../../components/SubmitButton";
import {formatDateTime} from "../../components/format";
import {SortHeader, useSort} from "../../components/sortable";
import {useAsync} from "../../components/useAsync";

// How often the emails' list is read again while one is sending, for its count.
const SENDING_POLL_MS = 5000;

const audienceName = (audience: NewsAudience, products: ProductViewModel[]) =>
    audience === 'news'
        ? 'Everyone who asked for news'
        : `Waiting for ${products.find(x => `release:${x.sku}` === audience)?.name ?? audience.substring('release:'.length)}`;

const inAudience = (audience: NewsAudience, subscriber: NewsSubscriberView) =>
    audience === 'news' ? subscriber.news : subscriber.releaseSkus.includes(audience.substring('release:'.length));

// The mailing list: who's signed up to hear when a product is released (and for other news), and the emails sent to
// them. Only confirmed sign-ups are listed; the API keeps nothing of the others.
export function MailingListPage() {
    const {data: products} = useAsync(() => ProductsApi.getPublished(), []);
    const list = useAsync(() => NewsApi.subscribers(), []);
    const campaigns = useAsync(() => NewsApi.campaigns(), []);
    const [editing, setEditing] = useState<NewsCampaign | 'new'>();
    const sending = campaigns.data?.some(x => x.status === 'sending');

    useEffect(() => {
        if (!sending) {
            return;
        }
        const timer = setInterval(() => campaigns.reload(), SENDING_POLL_MS);
        return () => clearInterval(timer);
    }, [sending]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <PageHeader title="Mailing list"/>
            <ErrorAlert error={list.error ?? campaigns.error}/>
            {(list.loading && !list.data) || !products ? <LoadingSpinner/> : list.data && (
                <>
                    {editing ? (
                        <CampaignEditor campaign={editing === 'new' ? undefined : editing} products={products} subscribers={list.data.subscribers}
                                        onClose={() => { setEditing(undefined); campaigns.reload(); }}/>
                    ) : (
                        <Campaigns campaigns={campaigns.data ?? []} products={products} onNew={() => setEditing('new')} onOpen={setEditing}/>
                    )}
                    <Subscribers subscribers={list.data.subscribers} suppressed={list.data.suppressed} products={products}
                                 onDeleted={id => list.setData({ ...list.data!, subscribers: list.data!.subscribers.filter(x => x.id !== id) })}/>
                </>
            )}
        </>
    );
}

function Subscribers({subscribers, suppressed, products, onDeleted}: {
    subscribers: NewsSubscriberView[], suppressed: number, products: ProductViewModel[], onDeleted(id: string): void
}) {
    const [filter, setFilter] = useState('');
    const [deleting, setDeleting] = useState<NewsSubscriberView>();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const productName = (sku: string) => products.find(x => x.sku === sku)?.name ?? sku;

    const shown = useMemo(() => {
        const text = filter.trim().toLowerCase();
        return subscribers.filter(x => !text || x.email.includes(text));
    }, [subscribers, filter]);
    const {sorted, sort, toggle} = useSort('admin-mailing-list:sort', shown, {
        email: x => x.email,
        products: x => x.releaseSkus.map(productName).join(', '),
        news: x => x.news ? 1 : 0,
        source: x => x.source,
        confirmed: x => x.confirmedOn
    });

    const remove = async () => {
        if (!deleting) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            await NewsApi.deleteSubscriber(deleting.id);
            onDeleted(deleting.id);
            setDeleting(undefined);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card>
            <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                <span className="me-auto">Subscribers</span>
                <Button size="sm" variant="outline-secondary" onClick={() => downloadCsv(subscribers, productName)} disabled={!subscribers.length}>
                    <i className="bi bi-download me-1" aria-hidden="true"></i>Export CSV
                </Button>
            </Card.Header>
            <Card.Body>
                <p className="text-body-secondary">
                    {subscribers.length} confirmed
                    {products.map(p => `, ${subscribers.filter(x => x.releaseSkus.includes(p.sku)).length} waiting for ${p.name}`).join('')}
                    , {subscribers.filter(x => x.news).length} want news. {suppressed} {suppressed === 1 ? 'address' : 'addresses'} bounced
                    or complained, and won't be mailed again.
                </p>
                <ErrorAlert error={error}/>
                <InputGroup className="mb-3" style={{ maxWidth: '24rem' }}>
                    <InputGroup.Text><i className="bi bi-search" aria-hidden="true"></i></InputGroup.Text>
                    <Form.Control placeholder="Filter by email" value={filter} onChange={e => setFilter(e.target.value)} aria-label="Filter"/>
                </InputGroup>
                <Table responsive hover className="mb-0">
                    <thead>
                    <tr>
                        <SortHeader column="email" sort={sort} onSort={toggle}>Email</SortHeader>
                        <SortHeader column="products" sort={sort} onSort={toggle}>Waiting for</SortHeader>
                        <SortHeader column="news" sort={sort} onSort={toggle}>News</SortHeader>
                        <SortHeader column="source" sort={sort} onSort={toggle}>Signed up on</SortHeader>
                        <SortHeader column="confirmed" sort={sort} onSort={toggle}>Confirmed</SortHeader>
                        <th><span className="visually-hidden">Actions</span></th>
                    </tr>
                    </thead>
                    <tbody>
                    {sorted.map(x => (
                        <tr key={x.id}>
                            <td>{x.email}</td>
                            <td>{x.releaseSkus.map(productName).join(', ')}</td>
                            <td>{x.news ? 'Yes' : 'No'}</td>
                            <td>{x.source === 'home' ? 'Home page' : 'Product page'}</td>
                            <td>{formatDateTime(x.confirmedOn)}</td>
                            <td className="text-end">
                                <Button size="sm" variant="outline-danger" onClick={() => setDeleting(x)} aria-label={`Remove ${x.email}`}>
                                    <i className="bi bi-trash" aria-hidden="true"></i>
                                </Button>
                            </td>
                        </tr>
                    ))}
                    {!sorted.length && <tr><td colSpan={6} className="text-body-secondary">{subscribers.length ? 'No one matches.' : 'No one has signed up yet.'}</td></tr>}
                    </tbody>
                </Table>
            </Card.Body>
            <ConfirmModal show={!!deleting} title="Remove from the mailing list" confirmLabel="Remove" busy={busy}
                          onConfirm={remove} onCancel={() => setDeleting(undefined)}>
                Remove <strong>{deleting?.email}</strong> and everything we have about their sign-up? Use this when someone asks
                to be forgotten; anyone else can unsubscribe themselves from any email.
            </ConfirmModal>
        </Card>
    );
}

function Campaigns({campaigns, products, onNew, onOpen}: {
    campaigns: NewsCampaign[], products: ProductViewModel[], onNew(): void, onOpen(campaign: NewsCampaign): void
}) {
    return (
        <Card className="mb-4">
            <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                <span className="me-auto">Emails</span>
                <Button size="sm" onClick={onNew}><i className="bi bi-plus-lg me-1" aria-hidden="true"></i>New email</Button>
            </Card.Header>
            <Card.Body>
                <Table responsive hover className="mb-0">
                    <thead>
                    <tr><th>Subject</th><th>To</th><th>Status</th><th className="text-end">Sent</th><th>Created</th></tr>
                    </thead>
                    <tbody>
                    {campaigns.map(x => (
                        <tr key={x.id}>
                            <td><Button variant="link" className="p-0 text-start" onClick={() => onOpen(x)}>{x.subject}</Button></td>
                            <td>{audienceName(x.audience, products)}</td>
                            <td><CampaignStatus campaign={x}/></td>
                            <td className="text-end">
                                {x.status === 'draft' ? `${x.audienceSize ?? 0} waiting` : x.sent}
                                {x.failed > 0 && <span className="text-danger small ms-1">({x.failed} failed)</span>}
                            </td>
                            <td>{formatDateTime(x.createdOn)}</td>
                        </tr>
                    ))}
                    {!campaigns.length && <tr><td colSpan={5} className="text-body-secondary">No emails yet.</td></tr>}
                    </tbody>
                </Table>
            </Card.Body>
        </Card>
    );
}

function CampaignStatus({campaign}: { campaign: NewsCampaign }) {
    switch (campaign.status) {
        case 'draft':
            return <Badge bg="secondary">{campaign.tested ? 'Draft, tested' : 'Draft'}</Badge>;
        case 'sending':
            return <Badge bg="warning" text="dark">Sending</Badge>;
        case 'sent':
            return <Badge bg="success">Sent {formatDateTime(campaign.sentOn)}</Badge>;
    }
}

// Writing a draft, testing it on yourself, and sending it. Once it's sending it can't be changed: it's shown as it went out.
function CampaignEditor({campaign: initial, products, subscribers, onClose}: {
    campaign?: NewsCampaign, products: ProductViewModel[], subscribers: NewsSubscriberView[], onClose(): void
}) {
    const [campaign, setCampaign] = useState(initial);
    const [subject, setSubject] = useState(initial?.subject ?? '');
    const [html, setHtml] = useState(initial?.html ?? '');
    const [audience, setAudience] = useState<NewsAudience>(initial?.audience ?? (products[0] ? `release:${products[0].sku}` : 'news'));
    const [busy, setBusy] = useState<'save' | 'test' | 'send' | 'delete'>();
    const [error, setError] = useState<unknown>();
    const [notice, setNotice] = useState<string>();
    const [confirm, setConfirm] = useState<'send' | 'delete'>();
    const draft = !campaign || campaign.status === 'draft';
    const dirty = !campaign || subject !== campaign.subject || html !== campaign.html || audience !== campaign.audience;
    const recipients = subscribers.filter(x => inAudience(audience, x)).length;

    // While it's going out, its counts (and its status, once it's sent) come from the API.
    useEffect(() => {
        if (campaign?.status !== 'sending') {
            return;
        }
        const timer = setInterval(() => NewsApi.campaign(campaign.id).then(fresh => {
            setCampaign(fresh);
            if (fresh.status === 'sent') {
                setNotice(undefined);
            }
        }, () => undefined), SENDING_POLL_MS);
        return () => clearInterval(timer);
    }, [campaign?.id, campaign?.status]);

    const run = async (action: 'save' | 'test' | 'send' | 'delete', work: () => Promise<void>) => {
        setBusy(action);
        setError(undefined);
        setNotice(undefined);
        try {
            await work();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(undefined);
        }
    };

    // Saves what's changed; returns the campaign as the API has it.
    const save = async (): Promise<NewsCampaign> => {
        if (campaign && !dirty) {
            return campaign;
        }
        const model = { subject: subject.trim(), html, audience };
        const saved = campaign ? await NewsApi.updateCampaign(campaign.id, model) : await NewsApi.createCampaign(model);
        setCampaign(saved);
        setSubject(saved.subject);
        return saved;
    };

    const submit = (e: FormEvent) => {
        e.preventDefault();
        run('save', async () => { await save(); setNotice('Saved.'); });
    };
    const test = () => run('test', async () => {
        const saved = await save();
        setCampaign(await NewsApi.testCampaign(saved.id));
        setNotice('Test sent to your email address. Check it, then send it to the list.');
    });
    const send = () => run('send', async () => {
        try {
            setCampaign(await NewsApi.sendCampaign(campaign!.id));
        } catch (err) {
            // It finished meanwhile: show it as it is, rather than the refusal.
            if (err instanceof ApiError && err.code === 'campaign_sent') {
                setCampaign(await NewsApi.campaign(campaign!.id));
                setConfirm(undefined);
                return;
            }
            throw err;
        }
        setConfirm(undefined);
        setNotice('Sending. The count goes up as it goes out.');
    });
    const remove = () => run('delete', async () => {
        await NewsApi.deleteCampaign(campaign!.id);
        onClose();
    });

    return (
        <Card className="mb-4">
            <Card.Header className="d-flex align-items-center gap-2">
                <span className="me-auto">{campaign ? campaign.subject : 'New email'}</span>
                {campaign && <CampaignStatus campaign={campaign}/>}
                <Button size="sm" variant="outline-secondary" onClick={onClose}>Back to emails</Button>
            </Card.Header>
            <Card.Body>
                <Form noValidate onSubmit={submit}>
                    <ErrorAlert error={error} shownFields={['subject', 'html', 'audience']}/>
                    {notice && <Alert variant="success" dismissible onClose={() => setNotice(undefined)}>{notice}</Alert>}
                    {!draft && (
                        <Alert variant="info">
                            {campaign!.status === 'sending' ? 'Sending' : 'Sent'} to {campaign!.sent} so far
                            {campaign!.failed > 0 && `, ${campaign!.failed} failed`}. It can't be changed now.
                            {campaign!.status === 'sending' && (
                                <> If the count has stopped going up, <Button variant="link" className="p-0 align-baseline" onClick={send}>carry on sending</Button>:
                                    no one gets it twice.</>
                            )}
                        </Alert>
                    )}
                    <Field className="mb-3" controlId="campaign-audience" label="To" error={fieldError(error, 'audience')}>
                        <Form.Select value={audience} onChange={e => setAudience(e.target.value as NewsAudience)} disabled={!draft}>
                            {products.map(x => <option key={x.sku} value={`release:${x.sku}`}>{audienceName(`release:${x.sku}`, products)}</option>)}
                            <option value="news">{audienceName('news', products)}</option>
                        </Form.Select>
                        <Form.Text>{recipients} {recipients === 1 ? 'person' : 'people'} now.</Form.Text>
                    </Field>
                    <Field className="mb-3" controlId="campaign-subject" label="Subject" error={fieldError(error, 'subject')}>
                        <FieldControl value={subject} onChange={e => setSubject(e.target.value)} maxLength={150} disabled={!draft}/>
                    </Field>
                    <Form.Label id="campaign-body-label">Message</Form.Label>
                    {draft
                        ? <RichTextEditor labelledBy="campaign-body-label" value={html} onChange={setHtml}/>
                        : <div className="border rounded p-3" aria-labelledby="campaign-body-label"><RichText html={html}/></div>}
                    {fieldError(error, 'html') && <div className="invalid-feedback d-block">{fieldError(error, 'html')}</div>}
                    <p className="small text-body-secondary mt-2">
                        Every email ends with why they're getting it, an unsubscribe link, and our postal address. Links must
                        start with https://; images aren't sent.
                    </p>
                    {draft && (
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <SubmitButton busy={busy === 'save'} variant="outline-primary">Save draft</SubmitButton>
                            <Button variant="outline-primary" onClick={test} disabled={!!busy}>{busy === 'test' ? 'Sending test…' : 'Save and send me a test'}</Button>
                            <Button onClick={() => setConfirm('send')} disabled={!!busy || dirty || !campaign?.tested || recipients === 0}
                                    title={dirty || !campaign?.tested ? 'Send yourself a test of what it says now first' : undefined}>
                                Send to {recipients} {recipients === 1 ? 'person' : 'people'}
                            </Button>
                            {dirty && campaign && <span className="small text-body-secondary">Unsaved changes</span>}
                            {campaign && <Button variant="outline-danger" className="ms-auto" onClick={() => setConfirm('delete')} disabled={!!busy}>Delete draft</Button>}
                        </div>
                    )}
                </Form>
            </Card.Body>
            <ConfirmModal show={confirm === 'send'} title="Send to the mailing list" confirmLabel={`Send to ${recipients}`} variant="primary"
                          busy={busy === 'send'} onConfirm={send} onCancel={() => setConfirm(undefined)}>
                Send <strong>{subject}</strong> to {recipients} {recipients === 1 ? 'person' : 'people'} ({audienceName(audience, products).toLowerCase()})?
                This can't be undone.
            </ConfirmModal>
            <ConfirmModal show={confirm === 'delete'} title="Delete draft" confirmLabel="Delete" busy={busy === 'delete'}
                          onConfirm={remove} onCancel={() => setConfirm(undefined)}>
                Delete the draft <strong>{campaign?.subject}</strong>?
            </ConfirmModal>
        </Card>
    );
}

// The list as a spreadsheet: one row per subscriber. A cell that starts like a formula is prefixed with ' so
// spreadsheets show it rather than run it.
function downloadCsv(subscribers: NewsSubscriberView[], productName: (sku: string) => string) {
    const cell = (value: string) => {
        const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
        return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
    };
    const rows = [
        ['Email', 'Waiting for', 'News', 'Signed up on', 'Confirmed'],
        ...subscribers.map(x => [x.email, x.releaseSkus.map(productName).join('; '), x.news ? 'yes' : 'no', x.source, x.confirmedOn])
    ];
    const url = URL.createObjectURL(new Blob([rows.map(r => r.map(cell).join(',')).join('\r\n') + '\r\n'], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `mailing-list-${new Date().toISOString().substring(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
}
